import type {
  ClinicalTaskCategory,
  ClinicalTaskPriority,
} from '@eluve/clinical-tasks';
import type { IconName } from '@eluve/components';
import { ELUVE_FHIR_NAMESPACE } from '@eluve/plan-shapes';

// The domain vocabulary (categories, priorities, labels) and the task-owned
// FHIR systems come from the shared `@eluve/clinical-tasks` lib — ONE
// definition read by the backend extraction/review and this panel, so the two
// sides can't drift. This file re-exports them for the app's components and
// adds only the UI-specific mappings (icons, chip tints).
export {
  CLINICAL_TASK_CATEGORIES,
  CLINICAL_TASK_CATEGORY_LABELS,
  CLINICAL_TASK_CATEGORY_SYSTEM,
  CLINICAL_TASK_ID_SYSTEM,
  CLINICAL_TASK_PRIORITIES,
  CLINICAL_TASK_PRIORITY_LABELS,
  CLINICAL_TASK_STATUSES,
  CLINICAL_TASK_STATUS_LABELS,
  asClinicalTaskStatus,
  asPriority,
  categoryLabel,
  clinicalTaskIntentFor,
  clinicalTaskReviewTagCodeFor,
  type ClinicalTaskCategory,
  type ClinicalTaskPriority,
  type ClinicalTaskStatus,
} from '@eluve/clinical-tasks';

/** Matches the backend agent id (`CLINICAL_TASKS_AGENT.id`). */
export const CLINICAL_TASKS_AGENT_ID = 'clinical-tasks';

// Cross-agent marker owned by the mini-app framework, not this agent — built
// from the shared namespace constant (the frontend can't import the
// nestjs-medplum helpers). Same string every agent stamps.
export const DERIVED_FROM_APPOINTMENT_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/derived-from-appointment`;

// Extension carrying the extraction's supporting transcript quote — same
// string the backend stamps (agents/clinical-tasks/systems.ts); rebuilt here
// for the same import-boundary reason as above.
export const SUPPORTING_PASSAGE_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/supporting-passage`;

// Cross-agent review-status tag system — same framework marker the backend
// stamps (clinical-findings/systems.ts). Needed browser-side because an
// accepted-task status change is written browser-direct and must keep the
// pending/attested tag consistent with the new status.
export const REVIEW_STATUS_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/review-status`;

// Standard HL7 systems for the attestation Provenance a browser-direct
// lifecycle change writes — identical to the backend reject/attest path
// (clinical-findings/systems.ts), so every "who decided this, and when" audit
// record has the same shape regardless of which door made the write.
export const DATA_OPERATION_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-DataOperation';
export const PROVENANCE_PARTICIPANT_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/provenance-participant-type';

/** Chip icon per category. */
export const CLINICAL_TASK_CATEGORY_ICONS: Record<
  ClinicalTaskCategory,
  IconName
> = {
  'lab-order': 'FlaskConical',
  referral: 'UserPlus',
  'follow-up': 'CalendarClock',
  medication: 'Pill',
  'patient-instruction': 'ClipboardList',
  general: 'List',
};

export const categoryIcon = (category: string | null) =>
  CLINICAL_TASK_CATEGORY_ICONS[
    (category ?? 'general') as ClinicalTaskCategory
  ] ?? 'List';

/** Chip tint per priority — red-hot down to neutral. */
export const CLINICAL_TASK_PRIORITY_CLASSES: Record<
  ClinicalTaskPriority,
  string
> = {
  stat: 'bg-bg-negative-subtle text-text-negative-subtle',
  asap: 'bg-bg-negative-subtle text-text-negative-subtle',
  urgent: 'bg-bg-warning-subtle text-text-warning-strong',
  routine: 'bg-bg-tertiary text-text-secondary',
};
